<template>
    <div class="card-body table mb-0">
  
      <PageHeader v-if="showHeading"
        title="All Subscriptions"
        class="poppins fw-normal text-nowrap mt-2"
      />
  
      <GeneralTable
        :isProcessing="processing"
        :items="items"
        :fields="fields"
        :showEyeButton="true"
        :showActionButton="false"
        @showItem="showInvoice"
      />
  
      <b-modal ref="invoiceModal" hide-footer size="lg">
        <template #modal-header="{ close }">
        <div class="d-flex align-items-center w-100 invoice-modal-header">

          <h5 class="mb-0 fw-semibold">
            Invoice Details
          </h5>
          <div class="d-flex align-items-center ms-auto gap-3">
            <a
                v-if="invoiceData?.invoice_pdf"
                :href="invoiceData.invoice_pdf"
                target="_blank"
              >
                📄 View Invoice PDF
              </a>
            <button
              type="button"
              class="btn-close custom-close"
              @click="close()"
            ></button>

          </div>
        </div>
      </template>
        <div v-if="loading" class="text-center">
          <APILoader :loading="loading" />
        </div>
  
        <div v-else class="invoice-info">
          <div class="invoice-column">
            <p><strong>Customer Email:</strong> {{ invoiceData?.customer_email ?? "N/A" }}</p>
            <p><strong>Amount Paid:</strong> {{ formatAmount(invoiceData?.amount_paid) }}</p>
            <p><strong>Amount Remaining:</strong> {{ formatAmount(invoiceData?.amount_remaining) }}</p>
            <p><strong>Subscription End:</strong> {{ subscriptionEndDate }}</p>
            <p><strong>Discount:</strong> {{ discountType }}</p>
          </div>
  
          <div class="invoice-column">
            <p><strong>Status:</strong> {{ invoiceData?.status }}</p>
  
            <h6 class="mt-3">Items</h6>
            <div
              v-for="item in invoiceData?.lines?.data"
              :key="item.id"
              class="mb-2"
            >
              <p class="mb-0">{{ item.description }}</p>
              <small>{{ formatAmount(item.amount) }} × {{ item.quantity }}</small>
            </div>
  
            <!-- <div class="mt-3">
              <a
                v-if="invoiceData?.invoice_pdf"
                :href="invoiceData.invoice_pdf"
                target="_blank"
              >
                View Invoice PDF
              </a>
            </div> -->
          </div>
        </div>
      </b-modal>
  
      <Pagination
        :pagination="paginationLinks"
        :showing="pagination"
        @paginator="fetchSubscriptions"
      />
  
    </div>
  </template>
  
  <script>
  import GeneralTable from "@/components/MagicTable/GeneralTable.vue";
  import Pagination from "@/components/general/pagination.vue";
  import PageHeader from "@/components/static/page-header";
  
  export default {
    name: "UserSubscriptions",
  
    components: {
      GeneralTable,
      Pagination,
      PageHeader
    },
  
    props: {
      user: {
        type: Object,
        required: true
      },
      showHeading:{
        type: Boolean,
        default:true
      }
    },
  
    data() {
      return {
        processing: false,
        loading: false,
  
        items: [],
        fields: [
          { key: "name", label: "Plan", tdClass: 'align-middle' },
          { key: "price", label: "Price", tdClass: 'align-middle' },
          { key: "stripe_status", label: "Stripe Status", tdClass: 'align-middle' },
          { key: "interval", label: "Interval", tdClass: 'align-middle' },
          { key: "created_at", label: "Started On", tdClass: 'align-middle' },
          { key: "subscription_end_date", label: "End Date", tdClass: 'align-middle' },
          { key: "action" }
        ],
  
        pagination: {},
        paginationLinks: [],
  
        invoiceData: null,
        subscriptionEndDate: null,
        discountType: null
      };
    },
  
    mounted() {
      this.fetchSubscriptions();
    },
  
    methods: {
      fetchSubscriptions({ url = "subscriptions/list" } = {}) {
        this.processing = true;
  
        this.$axios
          .get(url, {
            params: {
              user_id: this.user.id
            }
          })
          .then((response) => {
            const payload = response.data.data;
  
            this.pagination = {
              from: payload.from,
              to: payload.to,
              total: payload.total
            };
  
            this.paginationLinks = payload.links;
  
            this.items = payload.data.map((sub) => ({
              id: sub.id,
              name: sub?.subscription_plan?.name ?? "—",
              stripe_status: sub.stripe_status,
              interval: sub?.subscription_plan ? sub?.subscription_plan?.interval ?? "Yearly" : "N/A",
              created_at: sub.created_at_formatted,
              subscription_end_date: sub.subscription_end_date? this.formatDateTime(sub.subscription_end_date): "N/A",              
              price: `$${sub?.subscription_plan?.amount ?? "0"}`,
            }));
  
            this.processing = false;
          })
          .catch((error) => {
            this.processing = false;
            this.handleErrorResponse(error.response, "error");
          });
      },
      formatDateTime(isoDate) {
            if (!isoDate) return "N/A";
            const date = new Date(isoDate);
            if (isNaN(date.getTime())) {
                return isoDate;
            }

            const options = {
                year: "numeric",
                month: "short",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            };

            return date.toLocaleString(undefined, options);
            },
      handlePageChange(page) {
        this.fetchSubscriptions({
          url: `subscriptions/list?page=${page}`
        });
      },
  
      showInvoice(subscriptionId) {
        this.loading = true;
        this.invoiceData = null;
  
        this.$axios
          .get("invoices", {
            params: {
              subscription_id: subscriptionId,
              user_id: this.user?.id
            }
          })
          .then((response) => {
            this.invoiceData = response.data.invoices;
            this.discountType = response.data.discountType;
            this.subscriptionEndDate = this.items.find(sub => sub.id === subscriptionId).subscription_end_date;
            this.loading = false;
            this.$refs.invoiceModal.show();
          })
          .catch(() => {
            this.loading = false;
          });
      },
  
      formatAmount(amount = 0) {
        return `$${(amount / 100).toFixed(2)}`;
      }
    }
  };
  </script>
  
  <style scoped>
  .invoice-info {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
  }
  
  .invoice-column {
    border: 1px solid #ddd;
    border-radius: 6px;
    padding: 12px;
  }
  
  @media (max-width: 768px) {
    .invoice-info {
      grid-template-columns: 1fr;
    }
  }
  </style>
  